import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ClusterListItem } from '@b4b/shared';
import { useSetRead, useSetSaved } from '../api/queries.js';
import CardMenu from './CardMenu.js';
import { useRelativeTime } from '../lib/time.js';
import { displaySummary, displayTitle } from '../lib/cardText.js';
import Placeholder from './Placeholder.js';
import TranslatedBadge from './TranslatedBadge.js';

export default function ClusterCard({
  item,
  focused = false,
  imageSize = 'md',
}: {
  item: ClusterListItem;
  focused?: boolean;
  /** `lg` = bigger thumbnail from `sm` up (topics layout), summary gets a third line */
  imageSize?: 'md' | 'lg';
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const relTime = useRelativeTime();
  const setRead = useSetRead();
  const setSaved = useSetSaved();
  const [imgFailed, setImgFailed] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const rep = item.representative;
  const extraSources = item.sources.length - 1;

  const openOriginal = (e: React.MouseEvent) => {
    e.stopPropagation();
    window.open(rep.url, '_blank', 'noopener');
    if (!item.read) setRead.mutate({ id: item.id, read: true });
  };

  return (
    <article
      onClick={() => navigate(`/c/${item.id}`)}
      data-cluster-id={item.id}
      className={`group relative flex cursor-pointer gap-3 rounded-2xl border bg-white p-3 shadow-sm transition hover:shadow-md dark:bg-neutral-900 ${
        focused
          ? 'border-accent ring-2 ring-accent/40'
          : 'border-neutral-200 dark:border-neutral-800'
      } ${item.read ? 'opacity-60' : ''}`}
    >
      <div
        className={`h-20 w-20 shrink-0 overflow-hidden rounded-xl ${imageSize === 'lg' ? 'sm:h-35 sm:w-35' : 'sm:h-24 sm:w-24'}`}
      >
        {rep.imageUrl && !imgFailed ? (
          <img
            src={rep.imageUrl}
            alt=""
            referrerPolicy="no-referrer"
            loading="lazy"
            onError={() => setImgFailed(true)}
            className="h-full w-full object-cover"
          />
        ) : (
          <Placeholder title={rep.title} faviconUrl={rep.faviconUrl} className="h-full w-full" />
        )}
      </div>

      <div className="min-w-0 flex-1">
        <h2 className="line-clamp-2 font-semibold leading-snug">{displayTitle(rep)}</h2>
        <p className="mt-0.5 flex flex-wrap items-center gap-x-1 text-xs text-neutral-500">
          <span>
            {rep.feedTitle ?? ''} · {relTime(rep.publishedAt)}
            {rep.readingTimeMin ? ` · ${t('feed.readingTime', { min: rep.readingTimeMin })}` : ''}
          </span>
          {rep.translation && <TranslatedBadge translation={rep.translation} />}
        </p>
        {displaySummary(rep) && (
          <p
            className={`mt-1 text-sm text-neutral-600 dark:text-neutral-400 ${imageSize === 'lg' ? 'line-clamp-2 sm:line-clamp-3' : 'line-clamp-2'}`}
          >
            {displaySummary(rep)}
          </p>
        )}
        {extraSources > 0 && (
          <p className="mt-1 text-xs text-neutral-500">
            <span className="mr-1 inline-block rounded-full bg-accent/10 px-2 py-0.5 font-medium text-accent">
              {t('feed.sourcesBadge', { count: extraSources })}
            </span>
            {t('feed.alsoOn')}{' '}
            {item.sources
              .filter((s) => s.feedId !== rep.feedId)
              .slice(0, 3)
              .map((s) => s.feedTitle)
              .filter(Boolean)
              .join(', ')}
            {extraSources > 3 ? ` (+${extraSources - 3})` : ''}
          </p>
        )}
      </div>

      {/* one narrow action column keeps the title wide on phones; save/read live in the ⋯ menu (and on s/m keys) */}
      <div className="flex flex-col items-center">
        <button
          onClick={(e) => {
            e.stopPropagation();
            setMenuOpen((v) => !v);
          }}
          aria-label={t('feed.actions')}
          aria-expanded={menuOpen}
          className="min-h-[44px] min-w-[44px] rounded-lg text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800"
        >
          ⋯
        </button>
        <button
          onClick={openOriginal}
          aria-label={t('feed.openOriginal')}
          className="min-h-[44px] min-w-[44px] rounded-lg text-neutral-400 hover:bg-neutral-100 hover:text-accent dark:hover:bg-neutral-800"
        >
          ↗
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            setSaved.mutate({ id: item.id, saved: !item.saved });
          }}
          aria-label={item.saved ? t('article.unsave') : t('article.save')}
          aria-pressed={item.saved}
          className={`min-h-[44px] min-w-[44px] rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 ${
            item.saved ? 'text-amber-500' : 'text-neutral-400'
          }`}
        >
          {item.saved ? '★' : '☆'}
        </button>
      </div>

      {menuOpen && <CardMenu item={item} onClose={() => setMenuOpen(false)} className="right-2 top-12" />}
    </article>
  );
}
